import _ from "underscore";
import ObjectSearchUtils from "./ObjectSearchUtils";

const { RelationFilter } = ObjectSearchUtils;

type ColumnWithMaybeForType = {
    forType?: number;
};

/**
 * Returns the first column whose `forType` tag disagrees with `expectedTypeId`,
 * or `null` if all tagged columns agree (or none are tagged).
 *
 * Callers use this to detect cross-type contamination: a column array tagged
 * for one type leaking into a code path that expects another type. Only the
 * first tagged column is reported; the caller decides what to do with it
 * (typically Sentry-log and abort).
 */
export const findMismatchedColumn = <T extends ColumnWithMaybeForType>(
    columns: readonly T[] | null | undefined,
    expectedTypeId: number
): T | null => {
    if (!columns) {
        return null;
    }
    const tagged = columns.find((col) => col?.forType !== undefined);
    if (!tagged || tagged.forType === expectedTypeId) {
        return null;
    }
    return tagged;
};

/**
 * Whether the component's state is consistent with the selected type prop.
 * Used to gate UI interactions during type-change transitions: when props
 * have updated to a new type but the async work to load the new type's
 * state hasn't completed yet, UI controls should be disabled to prevent
 * the user from initiating writes against stale state.
 *
 *   - With no type selected (`propsTypeId === 0`), there is nothing to
 *     transition between, so the state is considered settled.
 *   - Otherwise, state is settled iff `state.objectSearch.type` matches
 *     `propsTypeId` AND every column in `state.objectSearch.columns`
 *     either has no `forType` tag or has a tag matching `propsTypeId`.
 */
export const isTypeSettled = <T extends ColumnWithMaybeForType>(
    stateObjectSearchType: number | null | undefined,
    propsTypeId: number,
    stateColumns: readonly T[] | null | undefined
): boolean => {
    if (propsTypeId === 0) {
        return true;
    }
    if (stateObjectSearchType !== propsTypeId) {
        return false;
    }
    return (stateColumns ?? []).every(
        (col) => col?.forType === undefined || col?.forType === propsTypeId
    );
};

type AdvancedSettings = {
    associatedObjects?: readonly unknown[];
    selectedCategories?: Record<string, unknown>;
    [key: string]: unknown;
};

/**
 * Whether the user has made any non-default search settings. Used to decide
 * whether to show a "clear settings" affordance and to drive a few internal
 * load-path branches.
 *
 * Treats three sources as evidence of user-modified settings:
 *   - Any associated objects selected.
 *   - Any categories selected.
 *   - Any key in `defaults` whose current value is non-null/undefined AND
 *     differs from the default.
 *
 * `defaults` is taken as a parameter so the function has no dependency on
 * the constants module; production callers should pass
 * `ObjectSettingsConstants.DEFAULTS`.
 */
export const hasSearchSettings = (
    advancedSettings: AdvancedSettings,
    defaults: Record<string, unknown>
): boolean => {
    const hasAssociatedObjects = Boolean(
        advancedSettings.associatedObjects && advancedSettings.associatedObjects.length > 0
    );
    const hasSelectedCategory = Object.keys(advancedSettings.selectedCategories || {}).length > 0;
    const isDefault = Object.keys(defaults)
        .filter((key) => advancedSettings[key] != null)
        .every((key) => _.isEqual(defaults[key], advancedSettings[key]));
    return hasAssociatedObjects || hasSelectedCategory || !isDefault;
};

/**
 * Whether `loadObjects` should turn off the relation filter and refetch all
 * objects, given the just-arrived search result. Two conditions cause this:
 *   1. The caller explicitly hinted at fallback (e.g., the settings build-up
 *      path in `getUpdatedSettings` decided to bail to all-objects mode).
 *   2. The search returned empty AND the instance is still allowed to
 *      fall back AND the user hasn't manually toggled the relation filter
 *      AND the relation filter is currently active and toggleable.
 *
 * A strict relation overrides everything: when the relation is strict, no
 * fallback is permitted regardless of the other inputs.
 */
export const shouldFallbackToAllObjects = (params: {
    fallbackHint: boolean;
    allowFallback: boolean;
    newObjectsCount: number;
    total: number;
    isRelationFilterToggleable: boolean;
    manualToggle: boolean;
    relationStateInactive: boolean;
    hasStrictRelation: boolean;
}): boolean => {
    if (params.hasStrictRelation) {
        return false;
    }
    if (params.fallbackHint) {
        return true;
    }
    return (
        params.allowFallback &&
        params.newObjectsCount === 0 &&
        params.total === 0 &&
        params.isRelationFilterToggleable &&
        !params.manualToggle &&
        !params.relationStateInactive
    );
};

type TypeStateDefaults = {
    objectSearch?: { type?: number | null };
    [key: string]: unknown;
};

/**
 * Produce the default state slice for a given type. If we're transitioning
 * from one type to another and the carried-over `objectSearch` belongs to
 * neither (i.e., its `type` doesn't match the new target), replace it with
 * a fresh empty one supplied by the caller. The caller-supplied factory
 * keeps this function free of any dependency on the `ObjectSearch` model.
 */
export const getDefaultTypeSettings = <T extends TypeStateDefaults>(
    initDefaults: T,
    prevType: number | undefined,
    nextType: number,
    createEmptyObjectSearch: () => T["objectSearch"]
): T => {
    const defaults = { ...initDefaults };
    if (prevType !== undefined && prevType !== nextType) {
        if (nextType !== defaults.objectSearch?.type) {
            defaults.objectSearch = createEmptyObjectSearch();
        }
    }
    return defaults;
};

/**
 * Fields used by shouldLoadObjects. Typed loosely because the function
 * only does deep-equality comparison across them — call sites pass full
 * component props/state.
 */
type ShouldLoadObjectsState = {
    objectSearch: {
        type?: unknown;
        searchString?: unknown;
        columns?: unknown;
        defaultColumns?: unknown;
        userSearchFields?: unknown;
        allFields?: unknown;
        advancedSettings?: { selectedCategories?: unknown; [k: string]: unknown };
        excludeObjects?: unknown;
    };
    relatedObjects?: unknown;
    hasStrictRelation?: unknown;
    relationState?: unknown;
    availabilityState?: unknown;
    settingsChange?: unknown;
    loadObjectsAnyway?: unknown;
};

type ShouldLoadObjectsProps = {
    selectedType?: unknown;
    data?: {
        editedEntry?: unknown;
        timeLimit?: unknown;
        availableObjects?: unknown;
    };
    staticObjects?: unknown;
    occuringObjects?: unknown;
    selectedFluffyItem?: unknown;
};

/**
 * Whether a parent re-render should trigger a re-fetch of the object list.
 * Returns true if any of the search-relevant inputs has changed.
 *
 * `selectionListObjects` is the current value of getSelectionListObjects(props);
 * it's compared against `state.objectSearch.excludeObjects` to detect when the
 * selection-list pane drives a search update.
 */
export const shouldLoadObjects = (
    props: ShouldLoadObjectsProps,
    state: ShouldLoadObjectsState,
    prevProps: ShouldLoadObjectsProps,
    prevState: ShouldLoadObjectsState,
    selectionListObjects: readonly unknown[]
): boolean => {
    const os = state.objectSearch;
    const prevOs = prevState.objectSearch;
    return (
        os.type !== prevOs.type ||
        os.searchString !== prevOs.searchString ||
        !_.isEqual(state.relatedObjects, prevState.relatedObjects) ||
        state.hasStrictRelation !== prevState.hasStrictRelation ||
        !_.isEqual(os.columns, prevOs.columns) ||
        !_.isEqual(os.defaultColumns, prevOs.defaultColumns) ||
        !_.isEqual(os.userSearchFields, prevOs.userSearchFields) ||
        !_.isEqual(os.allFields, prevOs.allFields) ||
        !_.isEqual(os.advancedSettings, prevOs.advancedSettings) ||
        !_.isEqual(selectionListObjects, os.excludeObjects || []) ||
        !_.isEqual(state.relationState, prevState.relationState) ||
        state.availabilityState !== prevState.availabilityState ||
        (Boolean(props.data) && props.data?.editedEntry !== prevProps.data?.editedEntry) ||
        !_.isEqual(props.selectedType, prevProps.selectedType) ||
        (Boolean(props.data) && !_.isEqual(props.data?.timeLimit, prevProps.data?.timeLimit)) ||
        !_.isEqual(
            os.advancedSettings?.selectedCategories,
            prevOs.advancedSettings?.selectedCategories
        ) ||
        state.settingsChange !== prevState.settingsChange ||
        (Boolean(props.data) &&
            props.data?.availableObjects !== prevProps.data?.availableObjects) ||
        !_.isEqual(props.staticObjects, prevProps.staticObjects) ||
        !_.isEqual(props.occuringObjects, prevProps.occuringObjects) ||
        !_.isEqual(props.selectedFluffyItem, prevProps.selectedFluffyItem) ||
        state.loadObjectsAnyway !== prevState.loadObjectsAnyway
    );
};

/**
 * Whether an incremental (scroll / lazy) load must be skipped because the object
 * store it would merge into is about to be rebuilt.
 *
 * The store's rows are positioned for the selection-list exclusion that was in
 * effect when it was last rebuilt from the top (`storeExcludeObjects`, i.e.
 * `state.objectSearch.excludeObjects`). Incremental loads, however, run their
 * search with the LIVE selection list (`currentSelectionListObjects`, from
 * `getSelectionListObjects()`). When those disagree, the selection list has just
 * changed (an object was added/removed) and the incoming page describes a
 * different, usually shorter, list than the store. Merging it shifts rows into an
 * over-sized array and strands the previous tail object -- the recurring "last
 * object in the list shown twice" bug (DEV-14739 / DEV-14165 / SCHED-1178).
 *
 * The same prop change that moved the selection list also schedules a full reload
 * from the top (loadObjects(0)), which rebuilds the store correctly, so skipping
 * the incremental merge loses nothing. Once that reload commits, the store's
 * exclusion matches the live list again and incremental loads resume.
 *
 * Missing/empty exclusion and an empty selection list are treated as equal so a
 * freshly-built store on an empty selection isn't read as a pending change.
 */
export const isObjectStoreStaleForMerge = (
    storeExcludeObjects: readonly unknown[] | undefined,
    currentSelectionListObjects: readonly unknown[]
): boolean => !_.isEqual(currentSelectionListObjects, storeExcludeObjects || []);

type ShouldLoadSuggestedObjectsState = {
    objectSearch?: { type?: unknown };
    relationState?: unknown;
    manualToggle?: boolean;
    loadObjectsAnyway?: boolean;
};

type ShouldLoadSuggestedObjectsProps = {
    selectedType?: unknown;
    selectedFluffyItem?: unknown;
    data?: unknown;
    relatedObject?: unknown;
};

/**
 * Whether suggestions (related objects / last-reserved objects) should be
 * re-fetched. Short-circuits cheap exclusions first:
 *   - No data sources at all (no fluffy item, no data, no related object) → no.
 *   - Filter has been manually turned off → no.
 *   - Filter just toggled or "load anyway" just became true → yes.
 * Otherwise re-fetch only if a tracked input has changed.
 */
export const shouldLoadSuggestedObjects = (
    props: ShouldLoadSuggestedObjectsProps,
    prevProps: ShouldLoadSuggestedObjectsProps,
    state: ShouldLoadSuggestedObjectsState,
    prevState: ShouldLoadSuggestedObjectsState,
    selectionListObjects: readonly unknown[],
    prevSelectionListObjects: readonly unknown[]
): boolean => {
    if (!props.selectedFluffyItem && !props.data && !props.relatedObject) {
        return false;
    }
    if (state.relationState === RelationFilter.INACTIVE && state.manualToggle) {
        return false;
    }
    if (state.relationState !== prevState.relationState) {
        return true;
    }
    if (state.loadObjectsAnyway === true && prevState.loadObjectsAnyway === false) {
        return true;
    }
    return (
        // A type change in the search state means state.relatedObjects belongs
        // to another type and must be refetched — even when no prop diff is
        // visible. Without this, a type-change flow that lost its prop-diff
        // context (e.g. a duplicate flow spawned by a late search commit)
        // routes to shouldLoadObjects, which falls back to all objects and
        // turns the relation filter off. See DEV-14580.
        state.objectSearch?.type !== prevState.objectSearch?.type ||
        !_.isEqual(selectionListObjects, prevSelectionListObjects) ||
        !_.isEqual(props.selectedType, prevProps.selectedType) ||
        !_.isEqual(props.selectedFluffyItem, prevProps.selectedFluffyItem) ||
        !_.isEqual(props.relatedObject, prevProps.relatedObject)
    );
};

type SortableColumn = {
    name?: string;
    sortable?: boolean;
};

export const isSortable = (
    columns: readonly SortableColumn[] | null | undefined,
    sortColumn: string
): boolean => {
    if (!columns) {
        return false;
    }
    const column = columns.find((col) => col?.name === sortColumn);
    return Boolean(column?.sortable);
};

export const isRelationFilterToggleable = (hasStrictRelation: boolean, typeId: number): boolean => {
    return !hasStrictRelation && typeId !== 0;
};

/**
 * Whether an object search should use the plain static fetch (getObjects on the
 * static id set, which ignores the query) rather than a server search.
 *
 *   - A non-static list never uses the static fetch (server search as before).
 *   - A plain static list always uses it (unchanged legacy behaviour).
 *   - A *searchable* static list runs a server search constrained to the static
 *     set instead, so the search string and filters apply — except when the set
 *     is empty, where the plain fetch correctly yields an empty result and
 *     avoids issuing an unconstrained (INCLUDE-with-empty-set) search.
 */
export const useStaticFetch = (params: {
    isStatic: boolean;
    staticSearchable: boolean;
    staticObjectCount: number;
}): boolean => {
    if (!params.isStatic) {
        return false;
    }
    if (params.staticSearchable && params.staticObjectCount > 0) {
        return false;
    }
    return true;
};

type FluffyItemWithKind = {
    typePhysical?: number;
};

export const isPhysicalType = (
    selectedFluffyItem: FluffyItemWithKind | null | undefined,
    physicalKinds: readonly number[]
): boolean => {
    if (!selectedFluffyItem) {
        return false;
    }
    const kind = selectedFluffyItem.typePhysical;
    return kind !== undefined && physicalKinds.indexOf(kind) !== -1;
};

export const isAvailabilityFilterAvailable = (
    hasTimeLimit: boolean,
    selectedFluffyItem: FluffyItemWithKind | null | undefined,
    physicalKinds: readonly number[]
): boolean => {
    return hasTimeLimit && isPhysicalType(selectedFluffyItem, physicalKinds);
};
